import React, { forwardRef, useEffect, useState } from 'react'
import { Grid, TextField } from '@mui/material'
import { makeStyles } from '@mui/styles'
import { StaticDatePicker, StaticDatePickerProps } from '@mui/x-date-pickers'
import classNames from 'classnames'
import { Moment } from 'moment'

import i18n from '../../../locales/i18n'
import moment from '../../../momentLocale'
import { Nil } from '../../../types'
import FilterButton from '../../buttons/FilterButton'

const useStyles = makeStyles(
  () => ({
    root: {
      position: 'relative',
    },
    todayButton: {
      width: 65,
      height: 33,
      position: 'absolute',
      top: 8,
      right: 0,
    },
    leftArrowButton: {
      position: 'absolute',
      top: 14,
      left: 0,
    },
    rightArrowButton: {
      position: 'absolute',
      top: 14,
      right: 0,
    },
    rightArrowButtonWithToday: {
      right: 70,
    },
  }),
  { name: 'StaticCalendar' },
)

const isToday = (date: Moment | Nil) =>
  date ? date.isSame(moment(), 'day') : false

export interface StaticCalendarProps<TDate = Moment>
  extends Omit<StaticDatePickerProps<TDate, TDate>, 'value'> {
  readonly showTodayButton?: boolean
  readonly showTopPickers?: boolean
  readonly value: TDate | null
}

const StaticCalendar = forwardRef<HTMLDivElement, StaticCalendarProps>(
  function StaticCalendar(
    {
      showTodayButton = true,
      disabled,
      renderInput,
      showTopPickers,
      ToolbarComponent,
      value,
      minDate,
      componentsProps: componentsPropsProp,
      onChange,
      ...rest
    },
    ref,
  ) {
    const classes = useStyles()
    const [todayButtonSelected, setTodayButtonSelected] = useState(
      isToday(value),
    )

    useEffect(() => {
      setTodayButtonSelected(isToday(value))
    }, [value])

    const onTodayButtonClick = () => {
      if (!todayButtonSelected) {
        onChange(moment())
      }
    }

    const renderTodayButton =
      showTodayButton && (!minDate || moment().isSameOrAfter(minDate))

    const componentsProps = {
      ...componentsPropsProp,
      actionBar: {
        actions: [],
      },
      leftArrowButton: {
        ...componentsPropsProp?.leftArrowButton,
        className: classes.leftArrowButton,
      },
      rightArrowButton: {
        ...componentsPropsProp?.rightArrowButton,
        className: classNames(classes.rightArrowButton, {
          [classes.rightArrowButtonWithToday]: renderTodayButton,
        }),
      },
    }

    return (
      <Grid container item className={classes.root}>
        {renderTodayButton && (
          <FilterButton
            className={classes.todayButton}
            isSelected={todayButtonSelected}
            text={i18n.t<string>('Common:TODAY')}
            onClick={onTodayButtonClick}
          />
        )}
        <StaticDatePicker
          ToolbarComponent={ToolbarComponent}
          componentsProps={componentsProps}
          disabled={disabled}
          minDate={minDate}
          ref={ref}
          renderInput={renderInput || (props => <TextField {...props} />)}
          value={value}
          onChange={onChange}
          {...rest}
        />
      </Grid>
    )
  },
)

export default StaticCalendar
